import { useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ChevronRight, ChevronDown } from "lucide-react";

interface NavigationItemProps {
  label: string;
  code?: string;
  status?: "required" | "optional" | "conditional" | "notused";
  depth: number;
  isExpanded?: boolean;
  hasChildren?: boolean;
  isSelected: boolean;
  onToggleExpand?: () => void;
  onSelect: () => void;
  itemType:
    | "segment"
    | "loop"
    | "element"
    | "component"
    | "hl_level"
    | "alternate_group";
  discriminantCode?: string | null;
  discriminantValue?: string | null;
  discriminantPosition?: number | null;
  icon?: React.ReactNode;
  parentInfo?: string; // e.g., "child of S" for HL levels
}

export function NavigationItem({
  label,
  code,
  status,
  depth,
  isExpanded = false,
  hasChildren = false,
  isSelected,
  onToggleExpand,
  onSelect,
  itemType,
  discriminantCode,
  discriminantValue,
  discriminantPosition,
  icon,
  parentInfo,
}: NavigationItemProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [isSelected]);

  const statusBadge = status
    ? { required: "R", optional: "O", conditional: "C", notused: "N" }[status]
    : null;
  const statusColor = {
    required: "text-red-500",
    optional: "text-blue-500",
    conditional: "text-warning",
    notused: "text-muted-foreground",
  }[status ?? "optional"];

  // Build discriminant label: NM101='10'
  // For loop alternates, discriminantCode is the first segment's code
  // For segment alternates, we use the segment's own code
  const labelCode = discriminantCode || code;
  const discriminantLabel =
    discriminantValue && discriminantPosition && labelCode
      ? `${labelCode}${String(discriminantPosition).padStart(2, "0")}='${discriminantValue}'`
      : null;

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand?.();
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-1 py-0.5 px-1 cursor-pointer text-xs rounded hover:bg-muted/50",
        isSelected && "bg-muted",
        (itemType === "element" || itemType === "component") &&
          "text-muted-foreground",
        itemType === "hl_level" && "text-purple-600 font-medium",
        itemType === "alternate_group" && "text-purple-600",
      )}
      style={{ paddingLeft: `${depth * 12 + 4}px` }}
      onClick={onSelect}
    >
      {/* Expand/collapse chevron */}
      <div className="w-4 shrink-0">
        {hasChildren && (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label}`}
            onClick={handleChevronClick}
            className="p-0 hover:bg-muted rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        )}
      </div>

      {/* Optional icon */}
      {icon}

      {/* Code (segment/loop/element) */}
      {code && <span className="font-mono font-medium shrink-0">{code}</span>}

      {/* Discriminant label for alternates */}
      {discriminantLabel && (
        <span className="font-mono text-[10px] text-purple-600 bg-purple-50 px-1 rounded border border-purple-200 shrink-0">
          {discriminantLabel}
        </span>
      )}

      {/* Name */}
      <span className="truncate flex-1">{label}</span>

      {/* Parent info for HL levels */}
      {itemType === "hl_level" && parentInfo && (
        <span className="text-[9px] text-muted-foreground shrink-0">
          ({parentInfo})
        </span>
      )}

      {/* Status badge */}
      {statusBadge && (
        <span
          className={cn("font-mono text-[12px] shrink-0 pr-1", statusColor)}
        >
          {statusBadge}
        </span>
      )}
    </div>
  );
}

interface NavigationSectionHeaderProps {
  title: string;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function NavigationSectionHeader({
  title,
  isSelected = false,
  onSelect,
}: NavigationSectionHeaderProps) {
  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (onSelect && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1 mt-2 first:mt-0 rounded",
        onSelect && "cursor-pointer hover:bg-muted/50 hover:text-foreground",
        isSelected && "bg-muted text-foreground",
      )}
    >
      {title}
    </div>
  );
}
